import { api } from './api';

export interface FieldEntry {
  en?: string;
  cn?: string;
  examplesEn?: string[];
  examplesCn?: string[];
}

export interface PromptVersion {
  id: number;
  key: string;
  version: number;
  en: string;
  cn: string;
  /** Only for the `field_guidance` key: leaf → bilingual copy + examples. */
  data: Record<string, FieldEntry> | null;
  isActive: boolean;
  note: string | null;
  createdAt: string;
  updatedAt: string;
}

export const FIELD_GUIDANCE_KEY = 'field_guidance';

export interface PromptGroup {
  key: string;
  activeVersion: number | null;
  versions: PromptVersion[];
}

export type PromptVersionContent = Partial<
  Pick<PromptVersion, 'en' | 'cn' | 'data' | 'note'>
>;

/** One portable prompt row — the shape download produces and upload accepts. */
export type PromptExportRow = Pick<
  PromptVersion,
  'key' | 'version' | 'en' | 'cn' | 'data' | 'isActive' | 'note'
>;

export interface PromptExport {
  prompts: PromptExportRow[];
}

export interface ImportPromptsResponse {
  count: number;
  keys: number;
}

export const matchmakerPromptsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    listPrompts: builder.query<PromptGroup[], void>({
      query: () => ({ url: '/matchmaker-prompts', method: 'GET' }),
      providesTags: ['MatchmakerPrompts'],
    }),
    createPromptVersion: builder.mutation<
      PromptVersion,
      { key: string } & PromptVersionContent
    >({
      query: (body) => ({
        url: '/matchmaker-prompts',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['MatchmakerPrompts'],
    }),
    updatePromptVersion: builder.mutation<
      PromptVersion,
      { id: number } & PromptVersionContent
    >({
      query: ({ id, ...body }) => ({
        url: `/matchmaker-prompts/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['MatchmakerPrompts'],
    }),
    activatePromptVersion: builder.mutation<PromptVersion, number>({
      query: (id) => ({
        url: `/matchmaker-prompts/${id}/activate`,
        method: 'POST',
      }),
      invalidatesTags: ['MatchmakerPrompts'],
    }),
    deletePromptVersion: builder.mutation<void, number>({
      query: (id) => ({
        url: `/matchmaker-prompts/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['MatchmakerPrompts'],
    }),
    importPrompts: builder.mutation<ImportPromptsResponse, PromptExport>({
      query: (body) => ({
        url: '/matchmaker-prompts/import',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['MatchmakerPrompts'],
    }),
  }),
});

export const {
  useListPromptsQuery,
  useCreatePromptVersionMutation,
  useUpdatePromptVersionMutation,
  useActivatePromptVersionMutation,
  useDeletePromptVersionMutation,
  useImportPromptsMutation,
} = matchmakerPromptsApi;
